// src/pages/SystemConfiguration/components/RegionConfigForm.tsx
// Form component for Region Configuration with auto-fill logic

import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Row, Col, Label, Input, FormFeedback, Alert, Button, InputGroup, InputGroupText } from 'reactstrap';
import NumberInput from 'Components/Common/NumberInput';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { SystemConfiguration, RegionConfigData } from '../types';
import { TicketType } from 'pages/TicketTypeManagement/types';
import { updateRegionConfig } from 'slices/systemConfiguration/thunk';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('system_configuration');

// Mục 6 (README.md §7) — sinh ký hiệu tuần tự: A..Z, rồi AA..AZ, BA.. (bijective base-26).
const symbolAt = (index: number): string => {
  let n = index;
  let s = '';
  do {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return s;
};

// Miền Bắc: 100.000 vé/ký tự; Miền Nam & Trung: 1.000.000 vé/ký tự.
const symbolsDivisor = (region: string): number => (region.includes('Bắc') ? 100_000 : 1_000_000);

const generateSymbols = (region: string, quantityPerPeriod: number): string[] => {
  const count = Math.max(1, Math.ceil((quantityPerPeriod || 0) / symbolsDivisor(region)));
  return Array.from({ length: count }, (_, i) => symbolAt(i));
};

const parseSymbols = (raw?: string): string[] => {
  try {
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
};

interface RegionConfigFormProps {
  config: SystemConfiguration | null;
  loading: boolean;
  onSubmit?: (values: any) => void;
  onReset?: () => void;
  formikRef?: React.RefObject<any>;
  ticketTypes: TicketType[];
  ticketTypesLoading?: boolean;
}

const RegionConfigForm: React.FC<RegionConfigFormProps> = ({
  config,
  loading,
  onSubmit,
  onReset,
  formikRef,
  ticketTypes,
  ticketTypesLoading = false,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch<any>();

  // Validation schema — digitCount/drawRounds giờ auto-fill theo loại vé đã chọn (load động
  // từ ticket-type-management), không còn giới hạn cứng 5/6 hay 18/27 nữa
  const validationSchema = Yup.object({
    ticketTypeId: Yup.number()
      .nullable()
      .required(st('vui_long_chon_loai_ve_error')),
    digitCount: Yup.number()
      .required(st('vui_long_nhap_so_chu_so_error'))
      .min(1, st('so_chu_so_du_thuong_phai_error')),
    drawRounds: Yup.number()
      .required(st('vui_long_nhap_so_lan_quay_error'))
      .min(1, st('so_lan_quay_thuong_phai_lon_error')),
    quantityPerPeriod: Yup.number()
      .required(st('vui_long_nhap_so_luong_ve_error'))
      .min(1, st('so_luong_ve_tao_moi_ky_error')),
    requireSpecialSymbol: Yup.boolean().required(),
    defaultPrizePayoutCommissionRate: Yup.number()
      .typeError(st('hoa_hong_tra_thuong_phai_la_error'))
      .required(st('vui_long_nhap_hoa_hong_tra_error'))
      .min(0, st('hoa_hong_tra_thuong_khong_duoc_error'))
      .max(100, st('hoa_hong_tra_thuong_khong_duoc_error_2')),
    // Mục 8bis (README §8) — gộp từ 3 tab cũ.
    defaultCommissionRate: Yup.number()
      .typeError(st('ty_le_hoa_hong_phai_la_error'))
      .required(st('vui_long_nhap_ty_le_hoa_error'))
      .min(0, st('ty_le_hoa_hong_khong_duoc_error'))
      .max(100, st('ty_le_hoa_hong_khong_duoc_error_2')),
    defaultTicketPrice: Yup.number()
      .typeError(st('gia_ve_phai_la_so_error'))
      .required(st('vui_long_nhap_gia_ve_mac_error'))
      .min(0, st('gia_ve_khong_duoc_am_error')),
    taxThreshold: Yup.number()
      .typeError(st('nguong_thue_phai_la_so_error'))
      .required(st('vui_long_nhap_nguong_chiu_thue_error'))
      .min(0, st('nguong_thue_khong_duoc_am_error')),
    taxRate: Yup.number()
      .typeError(st('ty_le_chiu_thue_phai_la_error'))
      .required(st('vui_long_nhap_ty_le_chiu_error'))
      .min(0, st('ty_le_chiu_thue_khong_duoc_error'))
      .max(100, st('ty_le_chiu_thue_khong_duoc_error_2')),
    prizeClaimPersonalInfoThreshold: Yup.number()
      .typeError(st('nguong_thu_thap_thong_tin_ca_error'))
      .required(st('vui_long_nhap_nguong_thu_thap_error'))
      .min(0, st('nguong_thu_thap_thong_tin_ca_error_2')),
  });

    const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      region: config?.region || '',
      ticketTypeId: config?.ticketTypeId ?? null,
      digitCount: config?.digitCount || 6,
      drawRounds: config?.drawRounds || 18,
      quantityPerPeriod: config?.quantityPerPeriod || 0,
      requireSpecialSymbol: config?.requireSpecialSymbol || false,
      defaultPrizePayoutCommissionRate: config?.defaultPrizePayoutCommissionRate ?? 0,
      // Mục 8bis (README §8) — gộp từ 3 tab cũ (Hoa hồng / Giá vé / Ngưỡng thuế TNCN).
      defaultCommissionRate: config?.defaultCommissionRate ?? 0,
      defaultTicketPrice: config?.defaultTicketPrice ?? 0,
      taxThreshold: config?.taxThreshold ?? 0,
      taxRate: config?.taxRate ?? 0,
      prizeClaimPersonalInfoThreshold: config?.prizeClaimPersonalInfoThreshold ?? 1_000_000,
    },
    validationSchema,
    onSubmit: onSubmit || (async (values) => {
      const result = await dispatch(updateRegionConfig({
        region: values.region,
        ticketTypeId: values.ticketTypeId,
        digitCount: values.digitCount,
        drawRounds: values.drawRounds,
        quantityPerPeriod: values.quantityPerPeriod,
        requireSpecialSymbol: values.requireSpecialSymbol,
        defaultPrizePayoutCommissionRate: values.defaultPrizePayoutCommissionRate,
        defaultCommissionRate: values.defaultCommissionRate,
        defaultTicketPrice: values.defaultTicketPrice,
        taxThreshold: values.taxThreshold,
        taxRate: values.taxRate,
        prizeClaimPersonalInfoThreshold: values.prizeClaimPersonalInfoThreshold,
        // Mục 6 + 8bis — lưu danh sách ký hiệu hiện tại (đã tự gen theo số lượng vé + chỉnh tay nếu có).
        pregeneratedSymbols: JSON.stringify(symbols),
      }));

      if (updateRegionConfig.fulfilled.match(result)) {
        toast.success(st('da_luu_cau_hinh_vung_mien_message'));
      } else {
        toast.error((result.payload as string) || st('khong_the_luu_cau_hinh_vung_message'));
      }
    }),
  });
  
  // Mục 6 — danh sách ký hiệu (ký hiệu vé) gen sẵn; state cục bộ (không qua Formik) vì là mảng động.
  const [symbols, setSymbols] = useState<string[]>([]);
  const [newSymbol, setNewSymbol] = useState('');

  useEffect(() => {
    setSymbols(parseSymbols(config?.pregeneratedSymbols));
  }, [config?.pregeneratedSymbols]);

  // Expose formik instance via ref
  React.useImperativeHandle(formikRef, () => ({
    submitForm: validation.submitForm,
    resetForm: () => {
      (onReset || validation.resetForm)();
      setSymbols(parseSymbols(config?.pregeneratedSymbols));
      setNewSymbol('');
    },
  }));

  const suggestedSymbolCount = Math.max(
    1,
    Math.ceil((validation.values.quantityPerPeriod || 0) / symbolsDivisor(validation.values.region || '')),
  );

  const handleRegenerateSymbols = () => {
    setSymbols(generateSymbols(validation.values.region || '', validation.values.quantityPerPeriod || 0));
  };

  // Mục 8bis (README §8) — TỰ ĐỘNG gen lại dải ký tự ngay khi "Số lượng vé tạo mỗi kỳ" hoặc Miền thay
  // đổi (không cần bấm nút). Lần đầu (config vừa nạp) chỉ ghi nhận giá trị, KHÔNG regen — giữ nguyên
  // danh sách đã lưu trước đó.
  const prevSymbolInputsRef = React.useRef<{ qty: number; region: string } | null>(null);
  useEffect(() => {
    const qty = validation.values.quantityPerPeriod || 0;
    const region = validation.values.region || '';
    const prev = prevSymbolInputsRef.current;
    if (prev === null) {
      if (qty > 0 && region) prevSymbolInputsRef.current = { qty, region };
      return;
    }
    if (prev.qty === qty && prev.region === region) return;
    prevSymbolInputsRef.current = { qty, region };
    setSymbols(generateSymbols(region, qty));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validation.values.quantityPerPeriod, validation.values.region]);

  const handleAddSymbol = () => {
    const value = newSymbol.trim().toUpperCase();
    if (!value) return;
    setSymbols((prev) => (prev.includes(value) ? prev : [...prev, value]));
    setNewSymbol('');
  };

  const handleRemoveSymbol = (value: string) => {
    setSymbols((prev) => prev.filter((s) => s !== value));
  };
  
  // Chọn loại vé mặc định khi danh sách vừa load xong mà chưa có lựa chọn nào (map theo Id —
  // nếu cấu hình cũ đã lưu ticketTypeId thì giữ nguyên, chỉ fallback về tên khi config chỉ có
  // dữ liệu cũ trước migration ticketTypeId, cuối cùng mới chọn loại vé đầu tiên trong danh sách)
  useEffect(() => {
    if (validation.values.ticketTypeId || ticketTypes.length === 0) return;

    const fallback = ticketTypes.find(t => t.name === validation.values.region) || ticketTypes[0];
    validation.setFieldValue('ticketTypeId', fallback.id);
    validation.setFieldValue('region', fallback.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketTypes]);

  // Auto-fill dựa theo loại vé đã chọn (load động từ ticket-type-management).
  // QUAN TRỌNG: map theo ticketTypeId (Id ổn định), KHÔNG map theo tên — vì nếu loại vé bị đổi
  // tên ở màn Quản lý Loại Vé, so khớp theo tên sẽ làm mất lựa chọn đã lưu trước đó.
  // region vẫn lưu Tên loại vé (để hiển thị/gửi backend); ticketTypeId lưu Id để nơi khác
  // (VD: Lập kế hoạch phát hành) tra ngược lại đúng loại vé; digitCount và drawRounds lấy trực
  // tiếp từ cấu hình của loại vé đó.
  useEffect(() => {
    const selectedType = ticketTypes.find(t => t.id === validation.values.ticketTypeId);
    if (!selectedType) return;

    validation.setFieldValue('region', selectedType.name);
    validation.setFieldValue('digitCount', selectedType.digits);
    validation.setFieldValue('drawRounds', selectedType.drawRounds);
    validation.setFieldValue('requireSpecialSymbol', selectedType.digits === 5);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validation.values.ticketTypeId, ticketTypes]);

  // Số lượng vé tạo mỗi kỳ: khác với digitCount/drawRounds (khoá cứng theo loại vé), trường này
  // cho phép sửa tay ở màn System Configuration. Chỉ auto-fill lại theo mặc định của loại vé khi
  // người dùng THỰC SỰ đổi sang loại vé khác (prevTicketTypeIdRef đã có giá trị và khác id hiện tại)
  // hoặc khi cấu hình chưa từng lưu giá trị này — không ghi đè giá trị đã lưu mỗi lần load lại trang.
  const prevTicketTypeIdRef = React.useRef<number | null | undefined>(undefined);
  useEffect(() => {
    const selectedType = ticketTypes.find(t => t.id === validation.values.ticketTypeId);
    if (!selectedType) return;

    const isTypeSwitch = prevTicketTypeIdRef.current !== undefined && prevTicketTypeIdRef.current !== validation.values.ticketTypeId;
    const isFirstLoadWithoutSavedValue = prevTicketTypeIdRef.current === undefined && !config?.quantityPerPeriod;

    if (isTypeSwitch || isFirstLoadWithoutSavedValue) {
      validation.setFieldValue('quantityPerPeriod', selectedType.quantityPerPeriod);
    }
    prevTicketTypeIdRef.current = validation.values.ticketTypeId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validation.values.ticketTypeId, ticketTypes]);
  
  if (loading && !config) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{st('dang_tai_text')}</span>
        </div>
      </div>
    );
  }
  
  return (
    <form onSubmit={validation.handleSubmit}>
      <Alert color="info" className="mb-3">
        <i className="ri-information-line me-2"></i>
        {st('chon_loai_ve_de_he_thong_message')}
      </Alert>

      <Row>
        <Col md={6}>
          <div className="mb-3">
            <Label htmlFor="ticketTypeId" className="form-label">
              {st('lua_chon_loai_ve_label')} <span className="text-danger">*</span>
            </Label>
            <Input
              type="select"
              id="ticketTypeId"
              name="ticketTypeId"
              className="form-control"
              value={validation.values.ticketTypeId ?? ''}
              data-action-code="input-ticket-type"
              data-action-name="Lựa chọn loại vé"
              onChange={(e) => {
                const value = e.target.value;
                validation.setFieldValue('ticketTypeId', value ? Number(value) : null);
              }}
              onBlur={validation.handleBlur}
              disabled={ticketTypesLoading || !PageActionPermission.canEnable(location.pathname, 'input-ticket-type')}
              invalid={validation.touched.ticketTypeId && !!validation.errors.ticketTypeId}
            >
              <option value="" disabled>
                {ticketTypesLoading ? st('dang_tai_danh_sach_loai_ve_text') : st('chon_loai_ve_text')}
              </option>
              {ticketTypes.map(type => (
                <option key={type.id} value={type.id}>{type.name}</option>
              ))}
            </Input>
            {!ticketTypesLoading && ticketTypes.length === 0 && (
              <div className="form-text text-warning">
                <i className="ri-alert-line me-1"></i>
                {st('chua_co_loai_ve_nao_dang_text')}
              </div>
            )}
            {validation.touched.ticketTypeId && validation.errors.ticketTypeId && (
              <FormFeedback type="invalid">{validation.errors.ticketTypeId as string}</FormFeedback>
            )}
          </div>
        </Col>
        
        <Col md={6}>
          <div className="mb-3">
            <Label htmlFor="digitCount" className="form-label">
              {st('so_chu_so_du_thuong_label')} <span className="text-danger">*</span>
            </Label>
            <Input
              type="number"
              id="digitCount"
              name="digitCount"
              className="form-control"
              value={validation.values.digitCount}
              data-action-code="input-digit-count"
              data-action-name="Số chữ số dự thưởng"
              disabled
              readOnly
            />
            <div className="form-text">
              <i className="ri-lock-line me-1"></i>
              {st('tu_dong_dien_theo_loai_ve_text')}
            </div>
          </div>
        </Col>
      </Row>
      
      <Row>
        <Col md={6}>
          <div className="mb-3">
            <Label htmlFor="drawRounds" className="form-label">
              {st('so_lan_quay_thuong_label')} <span className="text-danger">*</span>
            </Label>
            <Input
              type="number"
              id="drawRounds"
              name="drawRounds"
              className="form-control"
              value={validation.values.drawRounds}
              data-action-code="input-draw-rounds"
              data-action-name="Số lần quay thưởng"
              disabled
              readOnly
            />
            <div className="form-text">
              <i className="ri-lock-line me-1"></i>
              {st('tu_dong_dien_theo_loai_ve_text')}
            </div>
          </div>
        </Col>
        
        <Col md={6}>
          <div className="mb-3">
            <div className="form-check form-switch form-switch-lg mt-4">
              <Input
                type="checkbox"
                className="form-check-input"
                id="requireSpecialSymbol"
                name="requireSpecialSymbol"
                checked={validation.values.requireSpecialSymbol}
                data-action-code="input-require-special-symbol"
                data-action-name="Yêu cầu ký tự đặc biệt"
                disabled
                readOnly
              />
              <Label className="form-check-label" htmlFor="requireSpecialSymbol">
                {st('yeu_cau_ky_tu_dac_biet_label')} <span className="text-danger">*</span>
              </Label>
            </div>
            <div className="form-text">
              <i className="ri-lock-line me-1"></i>
              {st('chi_ap_dung_cho_loai_ve_text')}
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <div className="mb-3">
            <Label htmlFor="quantityPerPeriod" className="form-label">
              {st('so_luong_ve_tao_moi_ky_label')} <span className="text-danger">*</span>
            </Label>
            <NumberInput
              id="quantityPerPeriod"
              name="quantityPerPeriod"
              className="form-control"
              value={validation.values.quantityPerPeriod}
              data-action-code="input-quantity-per-period"
              data-action-name="Số lượng vé tạo mỗi kỳ"
              disabled={!PageActionPermission.canEnable(location.pathname, 'input-quantity-per-period')}
              onChange={(val) => validation.setFieldValue('quantityPerPeriod', val)}
              onBlur={validation.handleBlur}
              invalid={validation.touched.quantityPerPeriod && !!validation.errors.quantityPerPeriod}
            />
            <div className="form-text">
              <i className="ri-information-line me-1"></i>
              {st('tu_dong_dien_theo_loai_ve_text_2')}
            </div>
            {validation.touched.quantityPerPeriod && validation.errors.quantityPerPeriod && (
              <FormFeedback type="invalid">{validation.errors.quantityPerPeriod as string}</FormFeedback>
            )}
          </div>
        </Col>

        <Col md={6}>
          <div className="mb-3">
            <Label htmlFor="defaultPrizePayoutCommissionRate" className="form-label">
              {st('hoa_hong_tra_thuong_mac_dinh_label')} <span className="text-danger">*</span>
            </Label>
            <InputGroup>
              <Input
                type="number"
                id="defaultPrizePayoutCommissionRate"
                name="defaultPrizePayoutCommissionRate"
                min={0}
                max={100}
                step="0.01"
                value={validation.values.defaultPrizePayoutCommissionRate}
                data-action-code="input-quantity-per-period"
                data-action-name="Hoa hồng trả thưởng mặc định"
                disabled={!PageActionPermission.canEnable(location.pathname, 'input-quantity-per-period')}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  !!(
                    validation.touched.defaultPrizePayoutCommissionRate &&
                    validation.errors.defaultPrizePayoutCommissionRate
                  )
                }
              />
              <InputGroupText>%</InputGroupText>
              {validation.touched.defaultPrizePayoutCommissionRate &&
                validation.errors.defaultPrizePayoutCommissionRate && (
                  <FormFeedback type="invalid" className="d-block">
                    {validation.errors.defaultPrizePayoutCommissionRate as string}
                  </FormFeedback>
                )}
            </InputGroup>
            <div className="form-text">
              <i className="ri-information-line me-1"></i>
              {st('chi_dung_auto_fill_khi_tao_text')}
            </div>
          </div>
        </Col>
      </Row>

      {/* Mục 8bis (README §8) — gộp 3 tab cũ: Hoa hồng mặc định · Giá vé mặc định · Ngưỡng thuế TNCN */}
      <hr className="my-3" />
      <h6 className="fw-semibold mb-2">{st('hoa_hong_gia_ve_thue_tncn_title')}</h6>
      <Row>
        <Col md={4}>
          <div className="mb-3">
            <Label htmlFor="defaultCommissionRate" className="form-label">
              {st('ty_le_hoa_hong_mac_dinh_label')} <span className="text-danger">*</span>
            </Label>
            <InputGroup>
              <Input
                type="number"
                id="defaultCommissionRate"
                name="defaultCommissionRate"
                min={0}
                max={100}
                step="0.01"
                value={validation.values.defaultCommissionRate}
                data-action-code="input-quantity-per-period"
                data-action-name="Tỷ lệ hoa hồng mặc định"
                disabled={!PageActionPermission.canEnable(location.pathname, 'input-quantity-per-period')}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={!!(validation.touched.defaultCommissionRate && validation.errors.defaultCommissionRate)}
              />
              <InputGroupText>%</InputGroupText>
              {validation.touched.defaultCommissionRate && validation.errors.defaultCommissionRate && (
                <FormFeedback type="invalid" className="d-block">
                  {validation.errors.defaultCommissionRate as string}
                </FormFeedback>
              )}
            </InputGroup>
            <div className="form-text">{st('prefill_ty_le_hoa_hong_khi_text')}</div>
          </div>
        </Col>
        <Col md={4}>
          <div className="mb-3">
            <Label htmlFor="defaultTicketPrice" className="form-label">
              {st('gia_ve_mac_dinh_label')} <span className="text-danger">*</span>
            </Label>
            <InputGroup>
              <Input
                type="number"
                id="defaultTicketPrice"
                name="defaultTicketPrice"
                min={0}
                step="1000"
                value={validation.values.defaultTicketPrice}
                data-action-code="input-quantity-per-period"
                data-action-name="Giá vé mặc định"
                disabled={!PageActionPermission.canEnable(location.pathname, 'input-quantity-per-period')}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={!!(validation.touched.defaultTicketPrice && validation.errors.defaultTicketPrice)}
              />
              <InputGroupText>{st('d_text')}</InputGroupText>
              {validation.touched.defaultTicketPrice && validation.errors.defaultTicketPrice && (
                <FormFeedback type="invalid" className="d-block">
                  {validation.errors.defaultTicketPrice as string}
                </FormFeedback>
              )}
            </InputGroup>
            <div className="form-text">{st('prefill_gia_ve_khi_lap_phieu_text')}</div>
          </div>
        </Col>
        <Col md={4}>
          <div className="mb-3">
            <Label htmlFor="prizeClaimPersonalInfoThreshold" className="form-label">
              {st('nguong_thu_thap_thong_tin_ca_label')} <span className="text-danger">*</span>
            </Label>
            <InputGroup>
              <Input
                type="number"
                id="prizeClaimPersonalInfoThreshold"
                name="prizeClaimPersonalInfoThreshold"
                min={0}
                step="1000"
                value={validation.values.prizeClaimPersonalInfoThreshold}
                data-action-code="input-quantity-per-period"
                data-action-name="Ngưỡng thu thập thông tin cá nhân"
                disabled={!PageActionPermission.canEnable(location.pathname, 'input-quantity-per-period')}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  !!(
                    validation.touched.prizeClaimPersonalInfoThreshold &&
                    validation.errors.prizeClaimPersonalInfoThreshold
                  )
                }
              />
              <InputGroupText>{st('d_text')}</InputGroupText>
              {validation.touched.prizeClaimPersonalInfoThreshold &&
                validation.errors.prizeClaimPersonalInfoThreshold && (
                  <FormFeedback type="invalid" className="d-block">
                    {validation.errors.prizeClaimPersonalInfoThreshold as string}
                  </FormFeedback>
                )}
            </InputGroup>
            <div className="form-text">
              {st('ho_so_tra_thuong_co_gia_text')}
            </div>
          </div>
        </Col>
      </Row>
      <Row>
        <Col md={4}>
          <div className="mb-3">
            <Label htmlFor="taxThreshold" className="form-label">
              {st('nguong_chiu_thue_tncn_label')} <span className="text-danger">*</span>
            </Label>
            <InputGroup>
              <Input
                type="number"
                id="taxThreshold"
                name="taxThreshold"
                min={0}
                step="1000"
                value={validation.values.taxThreshold}
                data-action-code="input-quantity-per-period"
                data-action-name="Ngưỡng chịu thuế TNCN"
                disabled={!PageActionPermission.canEnable(location.pathname, 'input-quantity-per-period')}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={!!(validation.touched.taxThreshold && validation.errors.taxThreshold)}
              />
              <InputGroupText>{st('d_text')}</InputGroupText>
              {validation.touched.taxThreshold && validation.errors.taxThreshold && (
                <FormFeedback type="invalid" className="d-block">
                  {validation.errors.taxThreshold as string}
                </FormFeedback>
              )}
            </InputGroup>
          </div>
        </Col>
        <Col md={4}>
          <div className="mb-3">
            <Label htmlFor="taxRate" className="form-label">
              {st('ty_le_chiu_thue_tncn_label')} <span className="text-danger">*</span>
            </Label>
            <InputGroup>
              <Input
                type="number"
                id="taxRate"
                name="taxRate"
                min={0}
                max={100}
                step="0.01"
                value={validation.values.taxRate}
                data-action-code="input-quantity-per-period"
                data-action-name="Tỷ lệ chịu thuế TNCN"
                disabled={!PageActionPermission.canEnable(location.pathname, 'input-quantity-per-period')}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={!!(validation.touched.taxRate && validation.errors.taxRate)}
              />
              <InputGroupText>%</InputGroupText>
              {validation.touched.taxRate && validation.errors.taxRate && (
                <FormFeedback type="invalid" className="d-block">
                  {validation.errors.taxRate as string}
                </FormFeedback>
              )}
            </InputGroup>
            <div className="form-text">
              {st('thue_ty_le_phan_gia_tri_text')}
            </div>
          </div>
        </Col>
      </Row>

      {/* Mục 6 (README.md §7) — dải ký tự (ký hiệu vé) gen sẵn cho công ty */}
      <div className="mb-2 mt-2 d-flex align-items-center justify-content-between flex-wrap gap-2">
        <Label className="form-label mb-0">{st('dai_ky_tu_gen_san_label')}</Label>
        <Button
          type="button"
          color="primary"
          outline
          size="sm"
          data-action-code="input-quantity-per-period"
          data-action-name="Gen lại dải ký tự theo công thức"
          disabled={!PageActionPermission.canEnable(location.pathname, 'input-quantity-per-period')}
          onClick={handleRegenerateSymbols}
        >
          <i className="ri-refresh-line align-bottom me-1"></i>
          {st('gen_lai_theo_cong_thuc_button')}{suggestedSymbolCount} {st('ky_tu_button')}
        </Button>
      </div>
      <div className="form-text mb-2">
        <i className="ri-information-line me-1"></i>
        {validation.values.region.includes('Bắc')
          ? st('mien_bac_100_000_ve_1_text')
          : st('mien_nam_trung_1_000_000_text')}{' '}
        {st('danh_sach_text')} <strong>{st('tu_dong_gen_lai_text')}</strong> {st('khi_doi_so_luong_ve_tao_text')} {Number(validation.values.quantityPerPeriod || 0).toLocaleString('vi-VN')} {st('ve_text')} {suggestedSymbolCount} {st('ky_tu_co_the_them_xoa_text')}
      </div>
      <div className="d-flex flex-wrap gap-2 mb-2">
        {symbols.length === 0 && (
          <span className="text-muted fs-13">{st('chua_co_ky_tu_nao_nhap_text')}</span>
        )}
        {symbols.map((s) => (
          <span key={s} className="badge bg-primary-subtle text-primary fs-13 d-flex align-items-center gap-1">
            {s}
            {PageActionPermission.canEnable(location.pathname, 'input-quantity-per-period') && (
              <i
                className="ri-close-line"
                role="button"
                aria-label={st('xoa_ky_tu_s_text', { s: s })}
                onClick={() => handleRemoveSymbol(s)}
              ></i>
            )}
          </span>
        ))}
      </div>
      {PageActionPermission.canEnable(location.pathname, 'input-quantity-per-period') && (
        <Row>
          <Col md={4}>
            <InputGroup size="sm">
              <Input
                type="text"
                placeholder={st('them_ky_tu_vd_aa_placeholder')}
                value={newSymbol}
                maxLength={4}
                onChange={(e) => setNewSymbol(e.target.value.toUpperCase())}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddSymbol();
                  }
                }}
              />
              <Button type="button" color="light" onClick={handleAddSymbol}>
                <i className="ri-add-line"></i>
              </Button>
            </InputGroup>
          </Col>
        </Row>
      )}

    </form>
  );
};

export default RegionConfigForm;